import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ProgramOrganizationType } from '../../models/program-organization.model';
import { BusinessRole } from '../../models/role.model';
import { RequestState, ResourceKey, State } from '../reducers/pd.reducer';

export const PD_FEATURE_KEY = 'pd';

export const selectPDFeature = createFeatureSelector<State>(PD_FEATURE_KEY);

// --- Generic Request Selectors ---
export const selectRequest = (key: ResourceKey) => createSelector(selectPDFeature, (state: State) => state.requests[key]);
export const selectLoading = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.loading);
export const selectLoaded = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.loaded);
export const selectError = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.error);

// ============================================
// User Selectors
// ============================================
export const selectUser = createSelector(selectPDFeature, (state: State) => state.user);

// ============================================
// Tabs Selectors
// ============================================
export const selectActiveTabIndex = createSelector(selectPDFeature, (state: State) => state.activeTabIndex);

// ============================================
// View Mode Selectors
// ============================================
export const selectViewMode = createSelector(selectPDFeature, (state: State) => state.viewMode);

// ============================================
// Breadcrumbs Selectors
// ============================================
export const selectBreadcrumbs = createSelector(selectPDFeature, (state: State) => state.breadcrumbs);

// ============================================
// Filters Selectors
// ============================================
export const selectPrograms = createSelector(selectPDFeature, (state: State) => state.programs);
export const selectProgramsLoaded = selectLoaded('filters');
export const selectProgramsLoading = selectLoading('filters');
export const selectOrganizations = createSelector(selectPDFeature, (state: State) => state.organization);
export const selectSites = createSelector(selectPDFeature, (state: State) => state.sites);
export const selectOrganizationsLoading = selectLoading('organization');
export const selectOrganizationsLoaded = selectLoaded('organization');
export const selectProgramFilters = createSelector(selectPDFeature, (state: State) => state.programFilters);
export const selectOrganizationFilters = createSelector(selectPDFeature, (state: State) => state.organizationFilters);
export const selectFilters = createSelector(
  selectViewMode,
  selectProgramFilters,
  selectOrganizationFilters,
  (viewMode, programFilters, organizationFilters) =>
    viewMode === ProgramOrganizationType.Organization ? organizationFilters : programFilters
);
export const selectProducts = createSelector(selectPDFeature, (state: State) => state.products);
export const selectProductsLoading = selectLoading('products');
export const selectProductsLoaded = selectLoaded('products');

// ============================================
// Roles Selectors
// ============================================
export const selectRoles = createSelector(selectPDFeature, (state: State) => state.roles);
export const selectRolesLoaded = selectLoaded('roles');
export const selectBusinessRoles = createSelector(selectRoles, roles => roles.map(r => r.businessRole));
export const selectHasBusinessRole = (role: BusinessRole) => createSelector(selectRoles, roles => roles.some(r => r.businessRole === role));

// ============================================
// Site Performance Events Selectors
// ============================================
export const selectSitePerformanceEvents = createSelector(selectPDFeature, (state: State) => state.sitePerformanceEvents);
export const selectSitePerformanceEventsLoaded = selectLoaded('sitePerformanceEvents');
export const selectSitePerformanceEventsLoading = selectLoading('sitePerformanceEvents');

// ============================================
// Site Performance Interval Data Selectors
// ============================================
export const selectSitePerformanceIntervalData = createSelector(selectPDFeature, (state: State) => state.sitePerformanceIntervalData);
export const selectSitePerformanceIntervalDataLoaded = selectLoaded('sitePerformanceIntervalData');
export const selectSitePerformanceIntervalDataLoading = selectLoading('sitePerformanceIntervalData');

// ============================================
// Product Performance Events Selectors
// ============================================
export const selectProductPerformanceEvents = createSelector(selectPDFeature, (state: State) => state.productPerformanceEvents);
export const selectProductPerformanceEventsLoaded = selectLoaded('productPerformanceEvents');
export const selectProductPerformanceEventsLoading = selectLoading('productPerformanceEvents');

export const selectProductSitePerformances = createSelector(selectPDFeature, (state: State) => state.productSitePerformances);
export const selectProductSitePerformancesLoading = selectLoading('productSitePerformances');


export const selectPerformanceEvent = createSelector(selectPDFeature, s => s.selectedPerformanceEvent);
export const selectSelectedEvent = createSelector(
  selectPDFeature,
  selectSitePerformanceEvents,
  selectProductPerformanceEvents,
  (state, siteEvents, productEvents) => {
    const sel = state.selectedPerformanceEvent;
    if (!sel) return null;
    if (sel.kind === 'site') {
      return siteEvents.find(e => e.eventId === sel.eventId && (sel.siteId == null || e.siteId === sel.siteId)) ?? null;
    }
    return productEvents.find(e => e.eventId === sel.eventId && (sel.productId == null || e.productId === sel.productId)) ?? null;
  }
);